import React, { Component } from 'react';
import { Subscription } from 'rxjs';
import { lazyInject } from '../../context/module';
import { LoginService } from '../../services/login.service';
import { WorkflowRecreateService, WorkflowSearchResult } from '../../services/workflow-recreate.service';
import { FileManagerService } from '../../services/file-manager.service';

interface WorkflowSearchState {
    query: string;
    results: WorkflowSearchResult[];
    loading: boolean;
    error: string | null;
    isAdmin: boolean;
}

/**
 * Admin-only workflow search and recreate component.
 * Hidden for non-admin users — no UI rendered at all.
 */
export class WorkflowSearchComponent extends Component<{}, WorkflowSearchState> {
    @lazyInject(WorkflowRecreateService) private recreateService: WorkflowRecreateService;
    @lazyInject(LoginService) private loginService: LoginService;
    @lazyInject(FileManagerService) private fileManagerService: FileManagerService;

    private sub: Subscription;

    state: WorkflowSearchState = {
        query: '',
        results: [],
        loading: false,
        error: null,
        isAdmin: false,
    };

    componentDidMount() {
        this.sub = this.loginService.onUserDetailsChanged.subscribe(details => {
            this.setState({ isAdmin: details?.admin === true });
        });
    }

    componentWillUnmount() {
        this.sub?.unsubscribe();
    }

    private onSearch = () => {
        const q = this.state.query.trim();
        if (!q) return;
        this.setState({ loading: true, error: null, results: [] });
        this.recreateService.searchWorkflows(q).subscribe({
            next: results => this.setState({ results, loading: false }),
            error: () => this.setState({ error: 'Search failed', loading: false }),
        });
    };

    private onRecreate = (workflowId: string) => {
        this.setState({ loading: true, error: null });
        this.recreateService.recreateWorkflow(workflowId).subscribe({
            next: fileUpload => {
                this.fileManagerService.addFileUpload(fileUpload);
                this.setState({ loading: false, results: [], query: '' });
            },
            error: err => this.setState({ error: err?.message || 'Recreate failed', loading: false }),
        });
    };

    private formatSize(bytes: number): string {
        if (bytes < 1024) return bytes + ' B';
        if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
        return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    }

    render() {
        if (!this.state.isAdmin) return null;

        return (
            <div style={{ marginBottom: '12px', padding: '8px 12px', background: '#fff3cd', borderRadius: '4px', fontSize: '13px' }}>
                <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                    <span style={{ fontWeight: 600, color: '#856404' }}>🔧 Admin</span>
                    <input
                        type="text"
                        placeholder="Workflow ID"
                        value={this.state.query}
                        onChange={e => this.setState({ query: e.target.value })}
                        onKeyDown={e => e.key === 'Enter' && this.onSearch()}
                        style={{ flex: 1, padding: '4px 8px', border: '1px solid #ccc', borderRadius: '3px', fontSize: '13px' }}
                    />
                    <button
                        onClick={this.onSearch}
                        disabled={this.state.loading}
                        style={{ padding: '4px 12px', fontSize: '13px', cursor: 'pointer' }}
                    >
                        {this.state.loading ? '...' : 'Search'}
                    </button>
                </div>

                {this.state.error && (
                    <div style={{ color: '#dc3545', marginTop: '4px' }}>{this.state.error}</div>
                )}

                {this.state.results.length > 0 && (
                    <table style={{ width: '100%', marginTop: '6px', fontSize: '12px', borderCollapse: 'collapse' }}>
                        <thead>
                            <tr style={{ borderBottom: '1px solid #ddd', textAlign: 'left' }}>
                                <th style={{ padding: '3px 6px' }}>File</th>
                                <th style={{ padding: '3px 6px' }}>From → To</th>
                                <th style={{ padding: '3px 6px' }}>Status</th>
                                <th style={{ padding: '3px 6px' }}>Date</th>
                                <th style={{ padding: '3px 6px' }}></th>
                            </tr>
                        </thead>
                        <tbody>
                            {this.state.results.map(r => (
                                <tr key={r.workflowId} style={{ borderBottom: '1px solid #eee' }}>
                                    <td style={{ padding: '3px 6px' }}>
                                        {r.uploadedFiles.map(f => f.readableFilename || f.filename).join(', ')}
                                    </td>
                                    <td style={{ padding: '3px 6px' }}>{r.fromType} → {r.toType}</td>
                                    <td style={{ padding: '3px 6px', color: r.failed ? '#dc3545' : '#28a745' }}>
                                        {r.failed ? 'Failed' : 'Completed'}
                                    </td>
                                    <td style={{ padding: '3px 6px' }}>
                                        {r.createdDate ? new Date(r.createdDate).toLocaleTimeString() : ''}
                                    </td>
                                    <td style={{ padding: '3px 6px' }}>
                                        <button
                                            onClick={() => this.onRecreate(r.workflowId)}
                                            style={{ fontSize: '12px', cursor: 'pointer' }}
                                        >
                                            Recreate
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        );
    }
}
